import AdminState from '../AdminState';
import React, { useState, useEffect, useRef } from "react";
import "./MachineMenu.css";
import Backend from "../../../utils/api.js";
import { Button, Input } from '@chakra-ui/react';


// This represents a list of different options to choose from, such as labs or machines
// permissionFunction and checkPermissionFunction are optional parameters used to gray out unpermitted options
export default function MachineMenu({ uid, labId, selected, onClick, permissionFunction, checkPermissionFunction, }) {
    // Set the states for the component
    const [filterText, setFilterText] = useState("");
    const [data, setData] = useState([]);
    const [permissions, setPermissions] = useState(null);
    const [machineTypes, setMachineTypes] = useState({});
    const [toolCategories, setToolCategories] = useState({});
    const [serverCallFailed, setServerCallFailed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [retry, setRetry] = useState(0);

    // Selectable and unselectable (grayed out) data items
    const [selectableData, setSelectableData] = useState([]);
    const [unselectableData, setUnselectableData] = useState([]);

    // Ref for arrow key navigation
    const menuContainerRef = useRef(null);

    // User textbox that updates machine list accordingly
    const changeFilter = (text) => {
        setFilterText(text.target.value.toLowerCase())
    };

    // Handle arrow key navigation at container level
    const handleContainerKeyDown = (e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            const container = menuContainerRef.current;
            if (!container) return;

            // Find all focusable buttons (not disabled, not tabIndex="-1")
            const allButtons = Array.from(container.querySelectorAll('button'));
            const focusableButtons = allButtons.filter(btn =>
                !btn.disabled && btn.tabIndex !== -1
            );
            if (focusableButtons.length === 0) return;

            const currentIndex = focusableButtons.findIndex(btn => btn === document.activeElement);

            if (currentIndex === -1) {
                // No button is focused, focus the first one
                if (focusableButtons.length > 0) {
                    focusableButtons[0].focus();
                }
                return;
            }

            let nextIndex;
            if (e.key === 'ArrowUp') {
                nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableButtons.length - 1;
            } else {
                nextIndex = currentIndex < focusableButtons.length - 1 ? currentIndex + 1 : 0;
            }

            e.preventDefault();
            focusableButtons[nextIndex].focus();
        }
    };

    useEffect(() => {
        let active = true;
        setLoading(true);
        setServerCallFailed(false);
        async function load() {
            try {
                const responses = await Promise.all([
                    Backend.getMachineByLab(uid, labId),
                    Backend.getMachineTypes(uid, 1000),
                    Backend.getToolCategoriesByLab(uid, labId, 1000),
                    permissionFunction ? permissionFunction() : Promise.resolve({ status: 200, items: null }),
                ]);
                if (responses.some(response => response.status !== 200)) throw new Error('load_failed');
                if (!active) return;
                const [instances, types, categories, access] = responses;
                setData(instances.items || []);
                setMachineTypes(Object.fromEntries((types.items || []).map(type => [type.id, type.name])));
                setToolCategories(Object.fromEntries((categories.items || []).map(category => [category.categoryID, category.categoryName])));
                setPermissions(access.items);
            } catch {
                if (active) setServerCallFailed(true);
            } finally {
                if (active) setLoading(false);
            }
        }
        load();
        return () => { active = false; };
    }, [uid, labId, permissionFunction, retry]);

    // Set selectable and unselectable data
    useEffect(() => {
        let selectable = [];
        let unselectable = [];
        if (checkPermissionFunction === undefined) {
            selectable = data;
        } else {
            data.map((e) => {
                if (checkPermissionFunction(permissions, e)) {
                    selectable.push(e);
                } else {
                    unselectable.push(e);
                }
            });
        }

        setSelectableData(selectable);
        setUnselectableData(unselectable);
    }, [data, permissions, checkPermissionFunction]);

    if (loading) {
        return <AdminState status="loading" title="Loading tool instances…" />;
    }
    if (serverCallFailed) {
        return <AdminState status="error" title="Tool instances could not be loaded"
            description="Pinpoint could not load the tool instances and their permissions. Try again."
            onRetry={() => setRetry(value => value + 1)} />;
    } else {
        // Return a UI for the menu
        return (
            <div className="MachineMenu">
                <div className="FilterInput">
                    <Input
                        value={filterText}
                        variant="outline"
                        aria-label="Search tool instances"
                        placeholder="Search for a tool instance..."
                        onChange={changeFilter}
                    />
                </div>

                {data.length === 0 ? (
                    <AdminState title="No tool instances yet" description="Add a tool instance to start managing equipment in this space." />
                ) : !data.some(item => item.name.toLowerCase().includes(filterText)) ? (
                    <AdminState title="No matching tool instances" description="Try a different name or clear the search."
                        action={<Button variant="outline" size="sm" onClick={() => setFilterText('')}>Clear search</Button>} />
                ) : null}

                <div
                    className="MenuContent"
                    ref={menuContainerRef}
                    onKeyDown={handleContainerKeyDown}
                >
                    <ul>
                        {
                            // Items the user has permissions for
                            selectableData.filter(
                                datum => datum.name.toLowerCase().includes(filterText)
                            ).map((e) => {
                                // Get the name of the machine type
                                const machineType = e.machineType ? machineTypes[e.machineType] : null;

                                // Render a card for the machine
                                return (
                                    <MachineMenuEntry
                                        key={e.name}
                                        selected={selected === e ? true : false}
                                        onClick={() => onClick(e)}
                                        name={e.name}
                                        machineType={machineType}
                                        categoryName={toolCategories[e.categoryID] || "Uncategorized"}
                                        selectable={true}
                                    />
                                );
                            })
                        }
                        {
                            // Items the user doesn't have permissions for
                            unselectableData.filter(datum =>
                                datum.name.toLowerCase().includes(filterText)
                            ).map((e) => {
                                // Get the name of the machine type
                                const machineType = e.machineType ? machineTypes[e.machineType] : null;

                                // Render a card for the machine
                                return (
                                    <MachineMenuEntry
                                        key={e.name}
                                        selected={false}
                                        onClick={null}
                                        name={e.name}
                                        machineType={machineType}
                                        categoryName={toolCategories[e.categoryID] || "Uncategorized"}
                                        selectable={false}
                                    />
                                );
                            })
                        }
                    </ul>
                </div>
            </div>
        );
    }
}

// Component for the machine cards
function MachineMenuEntry(props) {
    const handleKeyDown = (e) => {
        if (props.onClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            props.onClick();
        }
        // Arrow keys are handled at container level
    };

    const buttonClass = `MachineMenuEntry ${props.selected ? 'selected' : 'unselected'} ${props.selectable ? 'selectable' : 'unselectable'}`;

    return (
        <li style={props.style}>
            {props.selectable ? (
                <button
                    onClick={props.onClick}
                    onKeyDown={handleKeyDown}
                    className={buttonClass}
                    aria-label={`Select tool instance ${props.name}${props.machineType ? `, tool: ${props.machineType}` : ''}, category: ${props.categoryName}`}
                    aria-pressed={props.selected}
                    type="button"
                >
                    <h3>{props.name}</h3>
                    <p>{[props.machineType, props.categoryName].filter(Boolean).join(' • ')}</p>
                </button>
            ) : (
                <button type="button" disabled
                    className={buttonClass}
                    aria-label={`Tool instance ${props.name}${props.machineType ? `, tool: ${props.machineType}` : ''}, category: ${props.categoryName} (not available)`}


                >
                    <h3>{props.name}</h3>
                    <p>{[props.machineType, props.categoryName].filter(Boolean).join(' • ')}</p>
                </button>
            )}
        </li>
    );
}
